import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Avatar } from '../ui/Avatar';
import { Spinner } from '../ui/Spinner';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useDelegateTicket } from '../../hooks/queries/useTickets';
import { ROLE_LABELS } from '../../services/auth/permissions';
import type { CollaboratorRole } from '../../services/auth/permissions';
import type { TicketWithRelations } from '../../services/tickets/ticketService';

interface TicketDelegateModalProps {
  ticket: TicketWithRelations;
  currentCollaboratorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketDelegateModal({
  ticket,
  currentCollaboratorId,
  open,
  onOpenChange,
}: TicketDelegateModalProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: collaborators, isLoading } = useCollaborators();
  const delegateTicket = useDelegateTicket();

  const filtered = (collaborators ?? [])
    .filter(
      (c) =>
        c.is_active &&
        c.id !== currentCollaboratorId &&
        c.full_name.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'fr'));

  function handleSubmit() {
    if (!selectedId) return;
    delegateTicket.mutate(
      { ticketId: ticket.id, delegateToId: selectedId },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40 animate-in fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-cime-surface rounded-cime shadow-xl border border-neutral-200 outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-cime-text-primary">
              Déléguer le ticket
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Context */}
          <div className="px-5 pt-3 pb-2">
            <p className="text-sm text-neutral-500">
              Vous resterez <span className="font-medium text-neutral-700">référent superviseur</span>.
              Le collaborateur choisi prendra en charge le suivi quotidien.
            </p>
          </div>

          {/* Search */}
          <div className="px-5 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Rechercher un collaborateur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* List */}
          <div className="px-3 pb-3 max-h-72 overflow-y-auto space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-neutral-400 text-center py-4">
                Aucun collaborateur trouvé
              </p>
            ) : (
              filtered.map((collab) => {
                const isSelected = selectedId === collab.id;
                return (
                  <button
                    key={collab.id}
                    type="button"
                    onClick={() => setSelectedId(isSelected ? null : collab.id)}
                    className={[
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                      isSelected
                        ? 'bg-cime-primary/10 border border-cime-primary'
                        : 'hover:bg-neutral-50 border border-transparent',
                    ].join(' ')}
                  >
                    <Avatar
                      name={collab.full_name}
                      src={collab.avatar_url ?? undefined}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">
                        {collab.full_name}
                      </p>
                      <p className="text-xs text-neutral-400 truncate">
                        {ROLE_LABELS[collab.role as CollaboratorRole] ?? collab.role}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-neutral-200">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm">
                Annuler
              </Button>
            </Dialog.Close>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!selectedId || delegateTicket.isPending}
              isLoading={delegateTicket.isPending}
            >
              Déléguer
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
